"use client"

import {
  useInfiniteQuery,
  useQuery,
  type UseInfiniteQueryResult,
  type UseQueryResult,
} from "@tanstack/react-query"

import type { ApiError } from "@/lib/api/client"

import { getCourse, listCohorts, listCourses } from "./api"
import type {
  CohortsResponse,
  CourseListResponse,
  CourseOut,
  UseCoursesListData,
  UseCoursesListParams,
} from "./types"

/**
 * Dedupe-by-id flattening of an infinite-query's accumulated pages.
 *
 * Planna's `/api/courses/` has no stable `ordering` parameter and an
 * undocumented default sort, so two paginated calls (including the
 * duplicate initial fetch React 19 issues in dev under StrictMode)
 * can return overlapping ids. Pass-through would surface those as
 * duplicate React keys.
 */
export function flattenCoursePages(
  pages: CourseListResponse[] | undefined,
): CourseOut[] {
  if (!pages) return []
  const seen = new Set<number>()
  const out: CourseOut[] = []
  for (const page of pages) {
    for (const course of page.items) {
      if (seen.has(course.id)) continue
      seen.add(course.id)
      out.push(course)
    }
  }
  return out
}

export const COURSES_PAGE_SIZE = 50

export function coursesInfiniteKey(q: string = "") {
  return ["courses", "infinite", q] as const
}

export type UseCoursesInfiniteResult = UseInfiniteQueryResult<
  { pages: CourseListResponse[]; pageParams: number[] },
  ApiError
>

export function useCoursesInfinite(q: string = ""): UseCoursesInfiniteResult {
  const trimmed = q.trim()
  return useInfiniteQuery<
    CourseListResponse,
    ApiError,
    { pages: CourseListResponse[]; pageParams: number[] },
    ReturnType<typeof coursesInfiniteKey>,
    number
  >({
    queryKey: coursesInfiniteKey(trimmed),
    queryFn: ({ pageParam, signal }) =>
      listCourses(
        {
          limit: COURSES_PAGE_SIZE,
          offset: pageParam,
          is_active: true,
          ...(trimmed ? { q: trimmed } : {}),
        },
        { signal },
      ),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length
      return nextOffset < lastPage.count ? nextOffset : undefined
    },
    staleTime: 5 * 60 * 1000,
  })
}

export function coursesListKey(params: UseCoursesListParams) {
  return ["courses", "list", params] as const
}

export function useCoursesList(
  params: UseCoursesListParams,
): UseQueryResult<UseCoursesListData, ApiError> {
  const { q, is_active, direction, domain, page, pageSize } = params
  return useQuery<UseCoursesListData, ApiError>({
    queryKey: coursesListKey(params),
    queryFn: async ({ signal }) => {
      const response = await listCourses(
        {
          q,
          is_active,
          direction,
          domain,
          limit: pageSize,
          offset: (page - 1) * pageSize,
        },
        { signal },
      )
      return {
        items: response.items,
        count: response.count,
        source: response.source,
      }
    },
    placeholderData: (prev) => prev,
    throwOnError: true,
  })
}

export function courseDetailKey(courseId: number | null) {
  return ["courses", "detail", courseId] as const
}

/**
 * Resolve a single course by id (live-first, cache-backed on the
 * server). Used by the invoice edit form to recover the course a draft
 * was issued against — its title for the combobox label and its native
 * `currency` for the cohort-price labels — when the course is not in the
 * loaded catalogue page. `throwOnError: false` so a Planna outage
 * (404/502) degrades to the catalogue/synthetic fallback instead of
 * crashing the detail page.
 */
export function useCourse(
  courseId: number | null,
): UseQueryResult<CourseOut, ApiError> {
  return useQuery<CourseOut, ApiError>({
    queryKey: courseDetailKey(courseId),
    queryFn: ({ signal }) => getCourse(courseId as number, { signal }),
    enabled: courseId != null,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    throwOnError: false,
  })
}

export function cohortsKey(
  courseId: number | null,
  includeCohortId?: number | null,
) {
  return ["courses", "cohorts", courseId, includeCohortId ?? null] as const
}

export function useCohorts(
  courseId: number | null,
  includeCohortId?: number | null,
): UseQueryResult<CohortsResponse, ApiError> {
  return useQuery<CohortsResponse, ApiError>({
    queryKey: cohortsKey(courseId, includeCohortId),
    queryFn: ({ signal }) =>
      listCohorts(courseId as number, includeCohortId, { signal }),
    enabled: courseId != null,
    // React 19 dev StrictMode mounts a hook twice. Keeping cohorts
    // fresh forever within the gcTime window means the second mount
    // serves from cache instead of issuing a duplicate network request
    // — both mounts otherwise land at Django through the dev rewrite.
    staleTime: Infinity,
    gcTime: 5 * 60 * 1000,
    refetchOnMount: false,
    throwOnError: false,
  })
}
